import { DownOutlined, UpOutlined } from '@ant-design/icons'
import fontkit from '@pdf-lib/fontkit'
import { Button, Col, Input, Modal, Row, Space, Spin, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import {
  adminApprovePrescription,
  adminRejectPrescription,
  approvePrescriptionByHealthInsurance,
  finalizedPrescriptionByHealthInsurance,
  getPrescriptionClaim,
  getPrescriptionClaimsList,
  getPrescriptionImage,
  rejectPrescriptionByHealthInsurance,
  updatePrescriptionMemo,
} from '../../api/delegatedClaims.api'
import { userState } from '../../store/user'

const orderStatusConfig = {
  paid: '입금완료',
  delivery_ready: '배송준비중',
  delivery_shipping: '배송중',
  delivery_complete: '배송완료',
  complete: '구매확정',
  cancel_request: '취소요청',
  cancel_complete: '취소완료',
  return_request: '반품요청',
  return_receive: '반품승인',
}

const PrescriptionsPage = () => {
  const [user] = useRecoilState(userState)
  const [prescriptionData, setPrescriptionData] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [detailData, setDetailData] = useState(undefined) // 상세 정보
  const [editingMemo, setEditingMemo] = useState({})
  const [tempMemo, setTempMemo] = useState({})
  const [loadingDetails, setLoadingDetails] = useState({}) // 로딩 상태 관리
  const [loadingStatus, setLoadingStatus] = useState({}) // 상태 변경 로딩 관리
  const [rejectModalVisible, setRejectModalVisible] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [currentRejectId, setCurrentRejectId] = useState(null)
  const [pageNumber, setPageNumber] = useState(0) // 페이지 번호 상태 추가
  const [modalVisible, setModalVisible] = useState(false) // 세금계산서 모달 상태 추가
  const [taxInvoiceData, setTaxInvoiceData] = useState(null) // 세금계산서 데이터 상태

  // 처방전 승인/반려 관련 상태
  const [
    healthInsuranceRejectModalVisible,
    setHealthInsuranceRejectModalVisible,
  ] = useState(false)
  const [healthInsuranceRejectReason, setHealthInsuranceRejectReason] =
    useState('')
  const [currentHealthInsuranceRejectId, setCurrentHealthInsuranceRejectId] =
    useState(null)

  useEffect(() => {
    const fetchData = async () => {
      await fetchPrescriptionClaimsList(pageNumber)
    }
    fetchData()
  }, [pageNumber])
  const fetchPrescriptionClaimsList = async (pageNumber = 0) => {
    try {
      const res = await getPrescriptionClaimsList({
        page: pageNumber,
        size: 10,
        sort: 'createdAt,desc',
      })

      if (res?.data) {
        // console.log('res', res.data)
        setPrescriptionData({
          content: res.data.content || [],
          pageable: res.data.pageable
            ? {
                pageNumber: res.data.pageable.pageNumber || pageNumber,
                pageSize: res.data.pageable.pageSize || 10,
              }
            : {
                pageNumber: pageNumber,
                pageSize: 10,
              },
          totalElements: res.data.totalElements || 0,
        })
      }
    } catch (error) {
      console.error('처방전 목록 조회 실패:', error)
    }
  }

  // 생년월일로 나이 구분 계산하는 함수 (19세 기준)
  const calculateAgeCategory = (birthday, today = dayjs()) => {
    if (!birthday) {
      return '-'
    }
    const formattedBirthday = `${birthday.substring(0, 2)}-${birthday.substring(
      2,
      4,
    )}-${birthday.substring(4, 6)}`
    const birthDate = dayjs(formattedBirthday)
    const age = today.diff(birthDate, 'year')

    return age >= 19 ? '19세 이상' : '19세 미만'
  }

  // 반려 모달 열기
  const handleRejectModalOpen = (recordId) => {
    setCurrentRejectId(recordId)
    setRejectReason('')
    setRejectModalVisible(true)
  }

  // 반려 모달 닫기
  const handleRejectModalClose = () => {
    setRejectModalVisible(false)
    setRejectReason('')
    setCurrentRejectId(null)
  }

  // 반려 처리 (관리자 반려)
  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      alert('반려 사유를 입력해주세요.')
      return
    }
    await handleAdminReject(currentRejectId)
  }
  // 처방전 관리자 승인
  const handleAdminApprove = async (recordId) => {
    setLoadingStatus({ ...loadingStatus, [recordId]: 'ADMIN_APPROVED' })

    try {
      await adminApprovePrescription({ id: recordId })
      await fetchPrescriptionClaimsList(pageNumber)
      alert('처방전이 승인되었습니다.')
    } catch (error) {
      console.error('관리자 승인 처리 실패:', error)
      alert('승인 처리 중 오류가 발생했습니다.')
    } finally {
      setLoadingStatus({ ...loadingStatus, [recordId]: false })
    }
  }

  // 처방전 관리자 반려
  const handleAdminReject = async (recordId) => {
    setLoadingStatus({ ...loadingStatus, [recordId]: 'ADMIN_REJECTED' })

    try {
      await adminRejectPrescription({
        id: recordId,
        rejectionReason: rejectReason,
      })
      await fetchPrescriptionClaimsList(pageNumber)
      handleRejectModalClose()
      alert('처방전이 반려되었습니다.')
    } catch (error) {
      console.error('관리자 반려 처리 실패:', error)
      alert('반려 처리 중 오류가 발생했습니다.')
    } finally {
      setLoadingStatus({ ...loadingStatus, [recordId]: false })
    }
  }

  // 처방전 공단 승인
  const handleHealthInsuranceApprove = async (recordId) => {
    setLoadingStatus({
      ...loadingStatus,
      [recordId]: 'HEALTH_INSURANCE_APPROVE',
    })

    try {
      await approvePrescriptionByHealthInsurance({ id: recordId })
      await fetchPrescriptionClaimsList(pageNumber)
      alert('처방전이 공단 승인되었습니다.')
    } catch (error) {
      console.error('공단 승인 처리 실패:', error)
      alert('공단 승인 처리 중 오류가 발생했습니다.')
    } finally {
      setLoadingStatus({ ...loadingStatus, [recordId]: false })
    }
  }

  // 처방전 공단 반려
  const handleHealthInsuranceReject = async (recordId) => {
    if (!healthInsuranceRejectReason.trim()) {
      alert('반려 사유를 입력해주세요.')
      return
    }

    setLoadingStatus({
      ...loadingStatus,
      [recordId]: 'HEALTH_INSURANCE_REJECT',
    })

    try {
      await rejectPrescriptionByHealthInsurance({
        id: recordId,
        rejectionReason: healthInsuranceRejectReason,
      })
      await fetchPrescriptionClaimsList(pageNumber)
      handleHealthInsuranceRejectModalClose()
      alert('처방전이 공단 반려되었습니다.')
    } catch (error) {
      console.error('공단 반려 처리 실패:', error)
      alert('공단 반려 처리 중 오류가 발생했습니다.')
    } finally {
      setLoadingStatus({ ...loadingStatus, [recordId]: false })
    }
  }

  // 처방전 최종 승인 (공단 반려 상태에서)
  const handleFinalizedApproval = async (recordId) => {
    setLoadingStatus({ ...loadingStatus, [recordId]: 'FINALIZED_APPROVE' })

    try {
      await finalizedPrescriptionByHealthInsurance({ id: recordId })
      await fetchPrescriptionClaimsList(pageNumber)
      alert('처방전이 최종 승인되었습니다.')
    } catch (error) {
      console.error('최종 승인 처리 실패:', error)
      alert('최종 승인 처리 중 오류가 발생했습니다.')
    } finally {
      setLoadingStatus({ ...loadingStatus, [recordId]: false })
    }
  }

  // 공단 반려 모달 열기
  const handleHealthInsuranceRejectModalOpen = (recordId) => {
    setCurrentHealthInsuranceRejectId(recordId)
    setHealthInsuranceRejectReason('')
    setHealthInsuranceRejectModalVisible(true)
  }

  // 공단 반려 모달 닫기
  const handleHealthInsuranceRejectModalClose = () => {
    setHealthInsuranceRejectModalVisible(false)
    setHealthInsuranceRejectReason('')
    setCurrentHealthInsuranceRejectId(null)
  }

  // 메모 업데이트 API 호출
  const handleMemoUpdate = async (recordId, newMemo) => {
    const record = prescriptionData?.content?.find(
      (item) => item.id === recordId,
    )
    if (!record) {
      console.log('Record not found for id:', recordId)
      return
    }

    try {
      await updatePrescriptionMemo({
        id: recordId,
        memo: newMemo,
      })
    } catch (error) {
      console.error('메모 업데이트 실패:', error)
      alert('메모 저장 중 오류가 발생했습니다.')
    }
  }

  // 메모 편집 시작
  const handleMemoEdit = (recordId, currentMemo) => {
    setEditingMemo({ ...editingMemo, [recordId]: true })
    setTempMemo({ ...tempMemo, [recordId]: currentMemo || '' })
  }
  const handleMemoCancel = (recordId) => {
    setEditingMemo({ ...editingMemo, [recordId]: false })
    setTempMemo({ ...tempMemo, [recordId]: '' })
  }
  const handleMemoSave = async (recordId) => {
    const newMemo = tempMemo[recordId]
    await handleMemoUpdate(recordId, newMemo)

    // detailData 업데이트해서 화면에 바로 반영
    if (detailData[recordId]) {
      setDetailData({
        ...detailData,
        [recordId]: {
          ...detailData[recordId],
          prescription: {
            ...detailData[recordId].prescription,
            adminSpecialNotes: newMemo,
          },
        },
      })
    }

    setEditingMemo({ ...editingMemo, [recordId]: false })
    setTempMemo({ ...tempMemo, [recordId]: '' })
  }

  // 테이블 컬럼 정의
  const columns = [
    {
      title: '상태값',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusConfig = {
          SUBMITTED: { text: '처방전 제출', color: '#F59E0B' },
          PURCHASED: { text: '구매완료', color: '#117FFA' },
          ADMIN_APPROVED: { text: '관리자 승인', color: '#00A4FF' },
          ADMIN_REJECTED: { text: '관리자 반려', color: '#EB516F' },
          REJECTED: { text: '공단 반려', color: '#EB516F' },
          FINALIZED: { text: '최종 완료', color: '#00A4FF' },
        }
        const config = statusConfig[status] || {
          text: status,
          color: '#6D747A',
        }

        return (
          <Tag
            color={config.color}
            style={{ color: '#FFF', fontWeight: 'bold' }}
          >
            {config.text}
          </Tag>
        )
      },
    },
    {
      title: 'No',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '신청자',
      dataIndex: 'applicantName',
      key: 'applicant',
      width: 100,
      render: (applicantName, record) => {
        return applicantName || record.recipientName || '-'
      },
    },
    {
      title: '수진자',
      dataIndex: 'patientName',
      key: 'patientName',
      width: 100,
      render: (patientName) => patientName || '-',
    },
    {
      title: '나이',
      dataIndex: 'patientBirthday',
      key: 'age',
      width: 100,
      render: (birthday) => {
        return birthday ? calculateAgeCategory(birthday) : '-'
      },
    },
    {
      title: '구매일자',
      dataIndex: 'createdAt',
      key: 'purchaseDate',
      width: 120,
      render: (createdAt) => {
        return createdAt ? dayjs(createdAt).format('YYYY-MM-DD') : '-'
      },
    },
    {
      title: '위임 만료일자',
      dataIndex: 'delegationExpiredAt',
      key: 'delegationClaimsExpiryDate',
      width: 140,
      render: (delegationExpiredAt) => {
        return delegationExpiredAt
          ? dayjs(delegationExpiredAt).format('YYYY-MM-DD')
          : '-'
      },
    },
    {
      title: '관리자 조치',
      key: 'managerActions',
      width: 180,
      render: (_, record) => {
        const isLoading = loadingStatus[record.id]

        // 관리자 처리 단계에서만 버튼 활성화
        const canManagerProcess =
          record.status === 'PURCHASED' || record.status === 'SUBMITTED'

        return (
          <Space>
            <Button
              size="small"
              type={
                record.status === 'ADMIN_APPROVED' ||
                record.status === 'FINALIZED'
                  ? 'primary'
                  : 'default'
              }
              style={{
                backgroundColor:
                  record.status === 'ADMIN_APPROVED' ||
                  record.status === 'FINALIZED'
                    ? '#00A4FF'
                    : canManagerProcess
                      ? '#FFF'
                      : '#F5F5F5',
                borderColor:
                  record.status === 'ADMIN_APPROVED' ||
                  record.status === 'FINALIZED'
                    ? '#00A4FF'
                    : canManagerProcess
                      ? '#A4ADB4'
                      : '#D9D9D9',
                color:
                  record.status === 'ADMIN_APPROVED' ||
                  record.status === 'FINALIZED'
                    ? '#FFF'
                    : canManagerProcess
                      ? '#6D747A'
                      : '#BFBFBF',
              }}
              loading={isLoading === 'ADMIN_APPROVED'}
              disabled={!canManagerProcess || isLoading}
              onClick={() => handleAdminApprove(record.id)}
            >
              관리자 승인
            </Button>
            <Button
              size="small"
              type={record.status === 'ADMIN_REJECTED' ? 'primary' : 'default'}
              style={{
                backgroundColor:
                  record.status === 'ADMIN_REJECTED'
                    ? '#EB516F'
                    : canManagerProcess
                      ? '#FFF'
                      : '#F5F5F5',
                borderColor:
                  record.status === 'ADMIN_REJECTED'
                    ? '#EB516F'
                    : canManagerProcess
                      ? '#A4ADB4'
                      : '#D9D9D9',
                color:
                  record.status === 'ADMIN_REJECTED'
                    ? '#FFF'
                    : canManagerProcess
                      ? '#6D747A'
                      : '#BFBFBF',
              }}
              loading={isLoading === 'ADMIN_REJECTED'}
              disabled={!canManagerProcess || isLoading}
              onClick={() => handleRejectModalOpen(record.id)}
            >
              관리자 반려
            </Button>
          </Space>
        )
      },
    },
    {
      title: '공단 조치',
      key: 'publicActions',
      width: 180,
      render: (_, record) => {
        const isLoading = loadingStatus[record.id]

        // 공단 처리 단계에서만 버튼 활성화 (관리자 승인 후)
        const canPublicProcess = record.status === 'ADMIN_APPROVED'
        const canComplete = record.status === 'REJECTED'

        // 공단 반려일 경우에 완료 처리 버튼 표시
        if (canComplete) {
          return (
            <Button
              size="small"
              type="primary"
              style={{
                backgroundColor: '#00A4FF',
                borderColor: '#00A4FF',
                alignSelf: 'center',
              }}
              loading={isLoading === 'FINALIZED'}
              disabled={isLoading}
              onClick={() => handleFinalizedApproval(record.id)}
            >
              최종 처리 완료
            </Button>
          )
        }

        return (
          <Space>
            <Button
              size="small"
              type={record.status === 'FINALIZED' ? 'primary' : 'default'}
              style={{
                backgroundColor:
                  record.status === 'FINALIZED'
                    ? '#00A4FF'
                    : canPublicProcess
                      ? '#FFF'
                      : '#F5F5F5',
                borderColor:
                  record.status === 'FINALIZED'
                    ? '#00A4FF'
                    : canPublicProcess
                      ? '#A4ADB4'
                      : '#D9D9D9',
                color:
                  record.status === 'FINALIZED'
                    ? '#FFF'
                    : canPublicProcess
                      ? '#6D747A'
                      : '#BFBFBF',
              }}
              loading={isLoading === 'HEALTH_INSURANCE_APPROVE'}
              disabled={!canPublicProcess || isLoading}
              onClick={() => handleHealthInsuranceApprove(record.id)}
            >
              최종 승인
            </Button>
            <Button
              size="small"
              type={record.status === 'REJECTED' ? 'primary' : 'default'}
              style={{
                backgroundColor:
                  record.status === 'REJECTED'
                    ? '#EB516F'
                    : canPublicProcess
                      ? '#FFF'
                      : '#F5F5F5',
                borderColor:
                  record.status === 'REJECTED'
                    ? '#EB516F'
                    : canPublicProcess
                      ? '#A4ADB4'
                      : '#D9D9D9',
                color:
                  record.status === 'REJECTED'
                    ? '#FFF'
                    : canPublicProcess
                      ? '#6D747A'
                      : '#BFBFBF',
              }}
              loading={isLoading === 'HEALTH_INSURANCE_REJECT'}
              disabled={!canPublicProcess || isLoading}
              onClick={() => handleHealthInsuranceRejectModalOpen(record.id)}
            >
              공단 반려
            </Button>
          </Space>
        )
      },
    },
    {
      title: '상세',
      key: 'detail',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          loading={loadingDetails[record.id]}
          icon={
            !loadingDetails[record.id] &&
            (expandedRowKeys.includes(record.id) ? (
              <UpOutlined />
            ) : (
              <DownOutlined />
            ))
          }
          onClick={() => handleRowExpand(record.id)}
        />
      ),
    },
  ]

  // 상세 정보 API 호출
  const fetchPrescriptionDetail = async (id) => {
    setLoadingDetails((prev) => ({ ...prev, [id]: true }))
    try {
      const res = await getPrescriptionClaim({ id })
      console.log('res', res)
      if (res?.data) {
        setDetailData((prev) => ({
          ...prev,
          [id]: res.data,
        }))
      }
    } catch (error) {
      console.error('상세 정보 조회 실패:', error)
      alert('상세 정보를 불러오는데 실패했습니다.')
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [id]: false }))
    }
  }

  // 행 확장/축소 핸들러
  const handleRowExpand = async (id) => {
    const isExpanding = !expandedRowKeys.includes(id)

    if (isExpanding) {
      // 확장할 때 상세 정보가 없으면 API 호출
      const record = prescriptionData?.content?.find((item) => item.id === id)
      if (record && !detailData?.[record?.id]) {
        console.log('record', record.id)
        const res = await fetchPrescriptionDetail(record?.id)
        console.log(' fetchPrescriptionDetail res', res)
      }
    }

    const newExpandedKeys = expandedRowKeys.includes(id)
      ? expandedRowKeys.filter((k) => k !== id)
      : [...expandedRowKeys, id]
    setExpandedRowKeys(newExpandedKeys)
  }

  // 확장된 행 렌더링
  const expandedRowRender = (record) => {
    const detail = detailData?.[record?.id]
    const isLoading = loadingDetails[record?.id]

    // 로딩 중일 때
    if (isLoading) {
      return (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#F6F7FA',
            borderRadius: '8px',
          }}
        >
          <Spin size="large" />
          <div style={{ color: '#6D747A', marginTop: '16px' }}>
            상세 정보를 불러오는 중...
          </div>
        </div>
      )
    }

    // 상세 정보가 없을 때
    if (!detail) {
      return (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#F6F7FA',
            borderRadius: '8px',
          }}
        >
          <div style={{ color: '#6D747A' }}>
            상세 정보를 불러올 수 없습니다.
          </div>
        </div>
      )
    }

    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: '#F6F7FA',
          borderRadius: '8px',
        }}
      >
        {/* 첫 번째 행: 구매 요약과 서류 처리 메모 */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '30px' }}>
          <div
            style={{
              flex: 1,
              border: '1px solid #A4ADB4',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            {/* 선택한 옵션 요약 or 구매 요약 */}
            <h4 style={{ color: '#191a1c', marginBottom: '16px' }}>
              {detail.purchaseInfo?.orderId ? '구매 요약' : '선택한 옵션 요약'}
            </h4>
            <div style={{ color: '#6D747A', lineHeight: '1.6' }}>
              <div style={{ color: 'red' }}>
                주문번호: {detail.purchaseInfo?.orderId || '구매 전'}
              </div>
              <div>구매일자: {detail.purchaseInfo?.createdAt || '-'}</div>
              <div>상품명: {detail.purchaseInfo?.productName || '-'}</div>
              <div>옵션/수량: {detail.purchaseInfo?.optionTitle || '-'}</div>
              <div>
                상품 카테고리: {detail.purchaseInfo?.categoryName || '-'} (
                {detail.purchaseInfo?.categorySubName || '-'})
              </div>
              <div>
                구매자:{' '}
                {detail.documents?.transactionStatement?.buyerName || '-'}
              </div>
              <div>
                총 금액:{' '}
                {detail.purchaseInfo?.totalPrice?.toLocaleString() || '-'} 원
              </div>
              <div>
                주문 상태:{' '}
                {orderStatusConfig[detail.purchaseInfo?.orderDetailStatus] ||
                  '-'}
              </div>
            </div>
          </div>
          <div
            style={{
              flex: 1,
              border: '1px solid #A4ADB4',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            {/* 서류 처리 메모 */}
            <h4 style={{ color: '#191a1c', marginBottom: '16px' }}>
              서류 처리 메모
            </h4>
            {editingMemo[record?.id] ? (
              <div style={{ marginBottom: '20px' }}>
                <Input.TextArea
                  value={tempMemo[record?.id] || ''}
                  onChange={(e) =>
                    setTempMemo({
                      ...tempMemo,
                      [record?.id]: e.target.value,
                    })
                  }
                  placeholder="메모를 입력해주세요"
                  rows={3}
                  style={{ marginBottom: '8px' }}
                />
                <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => handleMemoSave(record?.id)}
                    style={{
                      backgroundColor: '#00A4FF',
                      borderColor: '#00A4FF',
                    }}
                  >
                    완료
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleMemoCancel(record?.id)}
                  >
                    취소
                  </Button>
                </Space>
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <div
                  style={{
                    color: '#6D747A',
                    marginBottom: '8px',
                    minHeight: '60px',
                    padding: '8px',
                    backgroundColor: '#FFF',
                    borderRadius: '4px',
                    border: '1px solid #A4ADB4',
                  }}
                >
                  {detail?.prescription?.adminSpecialNotes ||
                    '메모가 없습니다.'}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    size="small"
                    onClick={() =>
                      handleMemoEdit(
                        record?.id,
                        detail?.prescription?.adminSpecialNotes,
                      )
                    }
                    style={{
                      backgroundColor: '#FFF',
                      borderColor: '#A4ADB4',
                      color: '#6D747A',
                    }}
                  >
                    메모 편집
                  </Button>
                </div>
              </div>
            )}

            {detail?.prescription?.adminRejectReason && (
              <div>
                <h4 style={{ color: 'red', marginBottom: '16px' }}>
                  관리자 반려 사유
                </h4>
                <div style={{ color: '#6D747A', lineHeight: '1.6' }}>
                  {detail?.prescription?.adminRejectReason ||
                    '관리자 반려 사유가 없습니다.'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 두 번째 행: 다운로드 버튼들 */}
        {detail.documents && (
          <div
            style={{
              padding: '20px',
              backgroundColor: '#F6F7FA',
              borderRadius: '8px',
              border: '1px solid #A4ADB4',
            }}
          >
            <h4
              style={{
                color: '#191a1c',
                margin: '0 auto 16px auto',
              }}
            >
              첨부 서류
            </h4>
            <Row gutter={[16, 16]}>
              {detail.documents.prescriptionImage && (
                <Col span={12}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '16px',
                      backgroundColor: '#FFF',
                      border: '1px solid #A4ADB4',
                      borderRadius: '8px',
                    }}
                  >
                    <span
                      style={{
                        color: '#6D747A',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      처방전
                    </span>
                    <Button
                      style={{
                        backgroundColor: '#FFF',
                        borderColor: '#A4ADB4',
                        color: '#6D747A',
                      }}
                      icon={<span>📄</span>}
                      onClick={() => {
                        handlePrescriptionImageDownload(
                          detail.documents?.prescriptionImage,
                        )
                      }}
                    >
                      다운로드
                    </Button>
                  </div>
                </Col>
              )}
              {detail.documents.taxInvoice && (
                <Col span={12}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '16px',
                      backgroundColor: '#FFF',
                      border: '1px solid #A4ADB4',
                      borderRadius: '8px',
                    }}
                  >
                    <span
                      style={{
                        color: '#6D747A',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      세금계산서
                    </span>
                    <Button
                      style={{
                        backgroundColor: '#FFF',
                        borderColor: '#A4ADB4',
                        color: '#6D747A',
                      }}
                      icon={<span>📄</span>}
                      onClick={() => {
                        const raw = detail.documents.taxInvoice
                        let parsed = raw
                        if (typeof raw === 'string') {
                          try {
                            parsed = JSON.parse(raw)
                          } catch (e) {
                            // 문자열(파일 URL 등)일 수 있음
                          }
                        }
                        console.log('세금계산서 모달 데이터:', parsed)

                        // base64 이미지 데이터가 있으면 새 창으로 띄우기
                        if (
                          parsed &&
                          parsed.fileData &&
                          parsed.contentType &&
                          parsed.fileName
                        ) {
                          openBase64ImageInNewWindow(
                            parsed.fileData,
                            parsed.contentType,
                            parsed.fileName,
                          )
                        } else {
                          // 기존처럼 모달 띄우기
                          setTaxInvoiceData(parsed)
                          setModalVisible(true)
                        }
                      }}
                    >
                      다운로드
                    </Button>
                  </div>
                </Col>
              )}
              {detail.documents.transactionStatement && (
                <Col span={12}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '16px',
                      backgroundColor: '#FFF',
                      border: '1px solid #A4ADB4',
                      borderRadius: '8px',
                    }}
                  >
                    <span
                      style={{
                        color: '#6D747A',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      거래명세서
                    </span>
                    <Button
                      style={{
                        backgroundColor: '#FFF',
                        borderColor: '#A4ADB4',
                        color: '#6D747A',
                      }}
                      icon={<span>📄</span>}
                      onClick={() =>
                        handleTransactionStatementPdf(
                          detail.documents.transactionStatement,
                        )
                      }
                    >
                      다운로드
                    </Button>
                  </div>
                </Col>
              )}
              {detail.documents.salesReceipt && (
                <Col span={12}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '16px',
                      backgroundColor: '#FFF',
                      border: '1px solid #A4ADB4',
                      borderRadius: '8px',
                    }}
                  >
                    <span
                      style={{
                        color: '#6D747A',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      매출 전표
                    </span>
                    <Button
                      style={{
                        backgroundColor: '#FFF',
                        borderColor: '#A4ADB4',
                        color: '#6D747A',
                      }}
                      icon={<span>📄</span>}
                      onClick={() =>
                        handleSalesReceipt(detail.documents.salesReceipt)
                      }
                    >
                      다운로드
                    </Button>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </div>
    )
  }

  // 거래명세서 서류
  const handleTransactionStatementPdf = async (t) => {
    try {
      const url = `${process.env.REACT_APP_S3_HOST}/transaction-statement/sugar-transcaction-tatement-form.pdf`
      const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer())

      const pdfDoc = await PDFDocument.load(existingPdfBytes)
      pdfDoc.registerFontkit(fontkit)
      let font
      try {
        const fontBytes = await fetch(
          '/assets/fonts/Pretendard-Regular.woff2',
        ).then((res) => res.arrayBuffer())
        font = await pdfDoc.embedFont(fontBytes)
      } catch {
        font = await pdfDoc.embedFont(StandardFonts.Helvetica)
      }

      const pages = pdfDoc.getPages()
      const firstPage = pages[0]
      const { height } = firstPage.getSize()
      const formattedPurchaseDate = dayjs(t.purchaseDate).format('YYYY.MM.DD')
      const formattedPurchaseAmount = t.totalPrice.toLocaleString()
      const textData = [
        { text: `${t.buyerName}`, x: 80, y: height / 2 + 308.5, size: 10 },
        {
          text: `${formattedPurchaseDate}`,
          x: 80,
          y: height / 2 + 288.5,
          size: 10,
        },
        { text: `${t.patientName}`, x: 80, y: height / 2 + 269.5, size: 10 },
        { text: `${t.quantity}`, x: 274, y: height / 2 + 150, size: 10 },
        { text: `${t.unitPrice}`, x: 310, y: height / 2 + 150, size: 10 },
        { text: `${t.totalPrice}`, x: 370, y: height / 2 + 150, size: 10 },
        {
          text: `${formattedPurchaseAmount} 원`,
          x: 510,
          y: height / 2 + 102,
          size: 13,
        },
      ]
      textData.forEach((item) => {
        firstPage.drawText(item.text, {
          x: item.x,
          y: item.y,
          size: item.size,
          font: font,
          color: rgb(0.102, 0.102, 0.102),
        })
      })

      const pdfBytes = await pdfDoc.save()
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const link = document.createElement('a')
      const today = dayjs().format('YYYY-MM-DD')
      link.href = URL.createObjectURL(blob)
      link.download = `거래명세서_${t.patientName}_${today}.pdf`
      link.click()
    } catch (error) {
      alert('PDF 수정 중 오류가 발생했습니다.')
    }
  }

  const handleSalesReceipt = async (salesReceipt) => {
    if (salesReceipt) {
      // base64 이미지 데이터인지 확인 (객체 형태로 오는 경우)
      if (typeof salesReceipt === 'object' && salesReceipt.fileData) {
        openBase64ImageInNewWindow(
          salesReceipt.fileData,
          salesReceipt.contentType,
          salesReceipt.fileName,
        )
      } else {
        // 기존 URL 형태로 처리
        window.open(salesReceipt, '_blank')
      }
    } else {
      alert('매출 전표 URL을 찾을 수 없습니다.')
    }
  }

  // 처방전 이미지 다운로드 함수
  const handlePrescriptionImageDownload = async (prescriptionImageData) => {
    if (!prescriptionImageData) {
      alert('처방전 이미지를 찾을 수 없습니다.')
      return
    }

    // accessToken이 있는 경우 (새로운 형태)
    if (
      prescriptionImageData &&
      typeof prescriptionImageData === 'object' &&
      prescriptionImageData.accessToken
    ) {
      try {
        const response = await getPrescriptionImage({
          token: prescriptionImageData.accessToken,
        })

        console.log('getPrescriptionImage response', response)

        if (response?.data) {
          // Blob 데이터를 URL로 변환하여 새 창으로 열기
          const blob = new Blob([response.data], {
            type: prescriptionImageData.contentType || 'image/jpeg',
          })
          const url = URL.createObjectURL(blob)
          const newWindow = window.open(url, '_blank')
          if (newWindow) {
            // 새 창이 열린 후 URL 해제
            setTimeout(() => URL.revokeObjectURL(url), 100)
          }
        }
      } catch (error) {
        console.error('처방전 이미지 조회 실패:', error)
        alert(
          '처방전 이미지를 불러오는 중 오류가 발생했습니다. 새로 고침 후 다시 시도해주세요!',
        )
      }
    }
    // 기존 base64 이미지 데이터인 경우
    else if (
      prescriptionImageData &&
      typeof prescriptionImageData === 'object' &&
      prescriptionImageData.fileData
    ) {
      openBase64ImageInNewWindow(
        prescriptionImageData.fileData,
        prescriptionImageData.contentType,
        prescriptionImageData.fileName,
      )
    }
    // 기존 URL 형태로 처리
    else if (prescriptionImageData) {
      window.open(
        `${process.env.REACT_APP_S3_HOST}/${prescriptionImageData}`,
        '_blank',
      )
    } else {
      alert('처방전 이미지를 찾을 수 없습니다.')
    }
  }

  // base64 이미지 데이터를 새 창으로 띄우는 함수
  const openBase64ImageInNewWindow = (fileData, contentType, fileName) => {
    try {
      // base64 데이터가 data URL 형태가 아니라면 data URL 형태로 만들기
      let dataUrl = fileData

      // base64 prefix가 없으면 추가
      if (!fileData.startsWith('data:')) {
        dataUrl = `data:${contentType || 'image/jpeg'};base64,${fileData}`
      }

      // 새 창 열기
      const newWindow = window.open('', '_blank')
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>${fileName || '이미지 뷰어'}</title>
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  background-color: #f5f5f5;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                }
                img {
                  max-width: 100%;
                  max-height: 100%;
                  border: 1px solid #ddd;
                  border-radius: 8px;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .container {
                  text-align: center;
                }
                .filename {
                  margin-bottom: 20px;
                  font-family: Arial, sans-serif;
                  color: #333;
                  font-size: 16px;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="filename">${fileName || '이미지 파일'}</div>
                <img src="${dataUrl}" alt="${fileName || '이미지'}" />
              </div>
            </body>
          </html>
        `)
        newWindow.document.close()
      } else {
        alert('새 창을 열 수 없습니다. 팝업 차단을 확인해주세요.')
      }
    } catch (error) {
      console.error('이미지 열기 오류:', error)
      alert('이미지를 열 수 없습니다.')
    }
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* 처방전 구매 관리 테이블 */}
      <div>
        <h2
          style={{
            color: '#191a1c',
            marginBottom: '20px',
            fontSize: '24px',
            fontWeight: 'bold',
          }}
        >
          처방전 구매 관리
        </h2>

        <Table
          columns={columns}
          dataSource={prescriptionData?.content}
          rowKey="id"
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: (expanded, record) => handleRowExpand(record.id),
            showExpandColumn: false,
          }}
          pagination={{
            pageSize: prescriptionData?.pageable?.pageSize || 10,
            current: (prescriptionData?.pageable?.pageNumber || 0) + 1,
            total: prescriptionData?.totalElements || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}건`,
            onChange: (page) => {
              const newPageNumber = page - 1 // Ant Design은 1부터 시작하므로 0부터 시작하도록 조정
              setPageNumber(newPageNumber)
            },
            onShowSizeChange: () => {
              setPageNumber(0) // 페이지 크기 변경 시 첫 페이지로 이동
            },
          }}
          scroll={{ x: 1200 }}
          style={{
            backgroundColor: '#FFF',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      </div>

      <Modal
        title="세금계산서"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        {taxInvoiceData ? (
          typeof taxInvoiceData === 'string' ? (
            <div style={{ color: '#6D747A', wordBreak: 'break-all' }}>
              원문: {taxInvoiceData}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ color: '#6D747A' }}>
                수진자: {taxInvoiceData.patientName || '-'}
              </div>
              <div style={{ color: '#6D747A' }}>
                생년월일: {taxInvoiceData.patientBirthday || '-'}
              </div>
              <div style={{ color: '#6D747A' }}>
                주민등록번호 뒷자리:{' '}
                {(taxInvoiceData.patientSsnBack &&
                  (taxInvoiceData.patientSsnBack || '-')) ||
                  '-'}
              </div>
              <div style={{ color: '#6D747A' }}>
                구매일자: {taxInvoiceData.purchaseDate || '-'}
              </div>
              <div style={{ color: '#6D747A' }}>
                상품명: {taxInvoiceData.productName || '-'}
              </div>
              <div style={{ color: '#6D747A' }}>
                옵션: {taxInvoiceData.optionTitle || '-'}
              </div>
              <div style={{ color: '#6D747A' }}>
                수량: {taxInvoiceData.optionQuantity ?? '-'}
              </div>
              <div style={{ color: '#6D747A' }}>
                총 금액: {(taxInvoiceData.totalPrice ?? 0).toLocaleString()} 원
              </div>
            </div>
          )
        ) : (
          <div style={{ color: '#6D747A' }}>
            표시할 세금계산서 데이터가 없습니다.
          </div>
        )}
      </Modal>

      {/* 관리자 반려 모달 */}
      <Modal
        title="처방전 반려 사유 입력"
        open={rejectModalVisible}
        onCancel={handleRejectModalClose}
        footer={[
          <Button key="cancel" onClick={handleRejectModalClose}>
            취소하기
          </Button>,
          <Button
            key="reject"
            type="primary"
            danger
            onClick={handleRejectConfirm}
            loading={loadingStatus[currentRejectId] === 'ADMIN_REJECTED'}
          >
            반려하기
          </Button>,
        ]}
        width={500}
      >
        <div style={{ margin: '16px 0' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            반려 사유를 입력해주세요.{'\n'} 해당 사유는 알림톡에 그대로
            반영되오니 상세히 입력해주세요.
          </label>
          <Input.TextArea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="반려 사유를 상세히 입력해주세요"
            rows={4}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>

      {/* 공단 반려 모달 */}
      <Modal
        title="공단 반려 사유 입력"
        open={healthInsuranceRejectModalVisible}
        onCancel={handleHealthInsuranceRejectModalClose}
        footer={[
          <Button key="cancel" onClick={handleHealthInsuranceRejectModalClose}>
            취소하기
          </Button>,
          <Button
            key="reject"
            type="primary"
            danger
            onClick={() =>
              handleHealthInsuranceReject(currentHealthInsuranceRejectId)
            }
            loading={
              loadingStatus[currentHealthInsuranceRejectId] ===
              'HEALTH_INSURANCE_REJECT'
            }
          >
            반려하기
          </Button>,
        ]}
        width={500}
      >
        <div style={{ margin: '16px 0' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            공단 반려 사유를 입력해주세요. {'\n'}해당 사유는 알림톡에 그대로
            반영되오니 상세히 입력해주세요.
          </label>
          <Input.TextArea
            value={healthInsuranceRejectReason}
            onChange={(e) => setHealthInsuranceRejectReason(e.target.value)}
            placeholder="공단 반려 사유를 상세히 입력해주세요"
            rows={4}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </div>
  )
}

export default PrescriptionsPage
